import React, { useState } from 'react';
import { TextInput, TouchableOpacity, View, Image as ReactImage, Switch, ActivityIndicator, ScrollView, Modal } from 'react-native';
import CustomButton from '../../../components/CustomButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MyAppText from '../../../components/MyAppText';
import { faAdd, faArrowLeft, faFile, faFileVideo } from '@fortawesome/free-solid-svg-icons';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import imageCompression from 'browser-image-compression';
import FinishReasonModal from '../../../components/app-agent/FinishReason';
import { appAgentService } from '../../../services/central-api/app-agent';
import { ClientError } from '../../../services/central-api/base-service';
import { WindowInformation } from '../../../services/window-information';
import FileUploader from '../../../components/FileUploader';
import { getFileTypeFromBuffer } from '../../../utils/getFileTypeFromBuffer';

const acceptedMimeTypes = ['application/pdf', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'video/mp4', 'image/jpeg', 'image/png'];

interface AgentOccurrenceReportParams {
    occurrence: Occurrence;
    involvedPeople: InvolvedPerson[];
    displaceReason: DisplaceReason | undefined;
    shouldFinishOccurrence: boolean;
    handcuffed: number;
    setStep: React.Dispatch<React.SetStateAction<'occurrenceDetail' | 'occurrenceReport' | 'occurrenceInvolved' | 'displaceQuestionary' | 'policeReport'>>;
    navigation: Navigation;
    actingBody: ActingBodyConfigurations | undefined;
    historic: OccurrenceReport[];
    rejectPoliceReport: boolean;
}

export default function AgentOccurrenceReport({ occurrence, involvedPeople, displaceReason, shouldFinishOccurrence, handcuffed, setStep, navigation, actingBody, historic, rejectPoliceReport }: AgentOccurrenceReportParams) {
    const windowInfo = WindowInformation();
    const { styles, theme } = useStyles(styleSheet);
    const [isScreenVisible, setIsScreenVisible] = useState<boolean>(true);
    const [historicNarrative, setHistoricNarrative] = useState({ dirty: false, value: '' });
    const [handcuffExplanation, setHandcuffExplanation] = useState({ dirty: false, value: '' });
    const [civilPoliceSupport, setCivilPoliceSupport] = useState(false);
    const [militaryPoliceSupport, setMilitaryPoliceSupport] = useState(false);
    const [files, setFiles] = useState<{ file: File; objectURI: string; key: string; }[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [finishStarter, setFinishStarter] = useState<boolean>(false);
    const [finishCategory, setFinishCategory] = useState<number>(0);
    const [finishSubCategory, setFinishSubCategory] = useState<number>(0);

    async function finishEvent() {
        try {
            if (!occurrence?.id) {
                return;
            }

            setIsLoading(true);

            for (const file of files) {
                try {
                    await appAgentService.createOccurrenceAttachment({ occurrenceId: occurrence.id, file: file.file, description: historicNarrative.value });
                } catch (err) {
                    console.error(err);
                }
            }

            if (shouldFinishOccurrence) {
                await appAgentService.finishOccurrence({
                    occurrenceId: occurrence.id,
                    report: historicNarrative.value,
                    handcuffExplanation: handcuffExplanation.value,
                    category: finishCategory,
                    subCategory: finishSubCategory,
                    civilPoliceSupport,
                    militaryPoliceSupport,
                    rejectPoliceReport
                });
            } else if (files.length === 0) {
                await appAgentService.addOccurrenceReport({ occurrenceId: occurrence.id, report: historicNarrative.value });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            clearOccurrenceReportForm();

            if (shouldFinishOccurrence) {
                navigation.navigate('AppAgent', { screen: 'AgentOccurrences' });
            } else {
                setStep('occurrenceDetail');
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    function clearOccurrenceReportForm() {
        setHistoricNarrative({ dirty: false, value: '' });
        setHandcuffExplanation({ dirty: false, value: '' });
        setFiles([]);
        setIsScreenVisible(false);
    }

    function disableConcludeButton(): boolean {
        if (historicNarrative.value == '' || (shouldFinishOccurrence && involvedPeople.filter(ip => ip.handcuffUsed).length > 0 && handcuffExplanation.value == '')) {
            return true;
        }

        if (actingBody && actingBody.needAttachmentToCloseOccurrence) {
            const hasAttachmentInPoliceReport = historic.filter(h => h.occurrenceReportType === 'attachment').length > 0;
            const hasUploadedFile = files.length > 0;
            return (shouldFinishOccurrence && !hasAttachmentInPoliceReport && !hasUploadedFile);
        }

        return isLoading;
    }

    function displayImageHint(): boolean {
        if (shouldFinishOccurrence && historicNarrative.value != '' && actingBody && actingBody.needAttachmentToCloseOccurrence) {
            const hasAttachmentInPoliceReport = historic.filter(h => h.occurrenceReportType === 'attachment').length > 0;
            const hasUploadedFile = files.length > 0;
            return (shouldFinishOccurrence && !hasAttachmentInPoliceReport && !hasUploadedFile);
        }

        return false;
    }

    const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }

        let file = e.target.files[0];

        const fileAlreadySelected = files.find(f => f.file.name === file.name && f.file.lastModified === file.lastModified);

        if (fileAlreadySelected) {
            return;
        }

        if (!file.type) {
            const mime = await getFileTypeFromBuffer(await file.arrayBuffer(), acceptedMimeTypes);

            if (!mime) {
                Toast.show({
                    type: 'sentinelxError',
                    text1: translate('appAgentFormatNotValid'),
                });
                return;
            }

            file = new File([file], file.name, { type: mime });
        }

        if (!acceptedMimeTypes.includes(file.type)) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('appAgentFormatNotValid'),
            });
            return;
        }

        if (!['image/jpeg', 'image/png'].includes(file.type)) {
            setFiles(current => [{ file, objectURI: URL.createObjectURL(file), key: String(Math.random()) }, ...current]);
            return;
        }

        const compressedFile = await imageCompression(file, {
            maxSizeMB: 2,
            maxWidthOrHeight: 1920,
        });

        setFiles(current => [{ file: compressedFile, objectURI: URL.createObjectURL(compressedFile), key: String(Math.random()) }, ...current]);
    };

    return (<>
        {!isLoading ?
            <Modal
                visible={isScreenVisible}
                onRequestClose={() => {
                    setIsScreenVisible(false);
                }}
                transparent={true}
            >
                <View style={{ position: 'absolute', bottom: 0, height: windowInfo.height - 64, width: windowInfo.width, flex: 1, flexDirection: 'column', backgroundColor: theme.colors.paginationBar.background }}>
                    <ScrollView contentContainerStyle={{ flex: 1 }}>
                        <View style={styles.container} >
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                <TouchableOpacity onPress={() => {
                                    clearOccurrenceReportForm();
                                    setIsScreenVisible(false);
                                    if (shouldFinishOccurrence && displaceReason) {
                                        setStep('displaceQuestionary');
                                    } else {
                                        setStep('occurrenceDetail');
                                    }
                                }}>
                                    <FontAwesomeIcon fontSize={30} icon={faArrowLeft} color={theme.colors.iconColor} />
                                </TouchableOpacity>
                            </View>
                            <View style={{ gap: 5 }}>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <MyAppText style={styles.alertText}>{translate(shouldFinishOccurrence ? 'finalHistoric' : 'addReport')}</MyAppText>
                                </View>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <TextInput
                                        placeholder={translate('addReport')}
                                        multiline={true}
                                        style={[
                                            styles.input,
                                            styles.multiline,
                                            historicNarrative.value == '' && historicNarrative.dirty ? styles.invalid : null,
                                            { color: historicNarrative.value ? theme.colors.textColor : theme.colors.placeholder }
                                        ]}
                                        value={historicNarrative.value}
                                        onBlur={() => {
                                            setHistoricNarrative({ dirty: true, value: historicNarrative.value });
                                        }}
                                        onChangeText={(value) => {
                                            setHistoricNarrative({ dirty: true, value });
                                        }}
                                    />
                                </View>
                                {shouldFinishOccurrence && handcuffed ?
                                    <>
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <MyAppText style={styles.alertText}>{translate('handcuffExplanation')}</MyAppText>
                                        </View>
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <TextInput
                                                placeholder={translate('handcuffExplanation')}
                                                multiline={true}
                                                style={[
                                                    styles.input,
                                                    styles.multiline,
                                                    handcuffExplanation.value == '' && handcuffExplanation.dirty ? styles.invalid : null,
                                                    { color: handcuffExplanation.value ? theme.colors.textColor : theme.colors.placeholder }
                                                ]}
                                                value={handcuffExplanation.value}
                                                onBlur={() => {
                                                    setHandcuffExplanation({ dirty: true, value: handcuffExplanation.value });
                                                }}
                                                onChangeText={(value) => {
                                                    setHandcuffExplanation({ dirty: true, value });
                                                }}
                                            />
                                        </View>
                                    </>
                                    : null}
                            </View>
                            <View style={{ gap: 5 }}>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <MyAppText style={styles.alertText}>{translate('addPicturesAndVideos')}</MyAppText>
                                </View>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, display: 'flex', flexWrap: 'wrap' }}>
                                    {files.map((file) =>

                                        file.file.type === 'image/jpeg' || file.file.type === 'image/png' ?
                                            <ReactImage
                                                key={file.key}
                                                style={[styles.blockSize, {
                                                    overflow: 'hidden',
                                                }]}
                                                source={{
                                                    uri: file.objectURI
                                                }}
                                            />
                                            : <View key={Math.random()} style={[styles.blockSize, {
                                                backgroundColor: theme.colors.primaryButton.background,
                                                justifyContent: 'center'
                                            }]}>
                                                <FontAwesomeIcon fontSize={30} icon={file.file.type === 'video/mp4' ? faFileVideo : faFile} color={theme.colors.iconColor} style={{ justifySelf: 'center', alignSelf: 'center' }} />
                                            </View>

                                    )}
                                    <FileUploader
                                        icon={faAdd}
                                        style={[styles.blockSize, styles.blockStyle, { width: 100 }]}
                                        contentContainerStyle={{ flex: 1 }}
                                        textStyle={{
                                            fontSize: 30,
                                            color: '#181A1B',
                                        }}
                                        onChange={handleFileInput}
                                    />
                                </View>
                                {displayImageHint() && <MyAppText style={styles.textHint}>{translate('needAttachmentToCloseOccurrence')}</MyAppText>}
                            </View>
                            {shouldFinishOccurrence ?
                                <>
                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <MyAppText style={styles.alertText}>{translate('receivedSupportFromOtherDepartments')}</MyAppText>
                                    </View>
                                    <View style={{ justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
                                        <View style={{ gap: 5, width: '100%' }}>
                                            <MyAppText>{translate('civilPolice')}</MyAppText>
                                            <Switch
                                                trackColor={{ false: '#767577', true: '#81b0ff' }}
                                                thumbColor={civilPoliceSupport ? '#f5dd4b' : '#f4f3f4'}
                                                onValueChange={setCivilPoliceSupport}
                                                value={civilPoliceSupport}
                                            />
                                        </View>
                                        <View style={{ gap: 5, width: '100%' }}>
                                            <MyAppText>{translate('militaryPolice')}</MyAppText>
                                            <Switch
                                                trackColor={{ false: '#767577', true: '#81b0ff' }}
                                                thumbColor={militaryPoliceSupport ? '#f5dd4b' : '#f4f3f4'}
                                                onValueChange={setMilitaryPoliceSupport}
                                                value={militaryPoliceSupport}
                                            />
                                        </View>
                                    </View>
                                </>
                                : null}
                            <CustomButton
                                style={{ marginBottom: 32 }}
                                onPress={() => {
                                    (occurrence && !occurrence.OccurrenceUnits?.find(unit => unit.OccurrenceUnit.occurrenceId == occurrence.id)?.OccurrenceUnit.isStarter) || !shouldFinishOccurrence ?
                                        finishEvent() :
                                        setFinishStarter(true);
                                }}
                                disabled={disableConcludeButton()}
                                text={translate(shouldFinishOccurrence ? 'conclude' : 'addReport').toUpperCase()}
                                textStyle={{
                                    fontSize: 16
                                }}
                                type='primary'
                            />
                        </View>
                    </ScrollView>
                </View>
            </Modal>
            : <View style={styles.loadingView}>
                <MyAppText style={styles.loadingText}>
                    {translate('processing')}...
                </MyAppText>
                <ActivityIndicator
                    animating={true}
                    style={styles.loading}
                    size='large'
                    color='#0071bc'
                >
                </ActivityIndicator>
            </View>
        }
        <FinishReasonModal
            setModalVisible={setFinishStarter}
            isModalVisible={finishStarter}
            finishCategory={finishCategory}
            finishSubCategory={finishSubCategory}
            setFinishCategory={setFinishCategory}
            setFinishSubCategory={setFinishSubCategory}
            onSubmit={async () => {
                await finishEvent();
            }}
        />
    </>
    );
}


const styleSheet = createStyleSheet((theme) => ({
    loading: {
        marginHorizontal: 'auto',
        transform: [{ scaleX: 2 }, { scaleY: 2 }],
        width: '20%',
        height: '20%',
    },
    loadingText: {
        color: theme.colors.loadingColor,
        textAlign: 'center',
        fontSize: 16
    },
    loadingView: {
        flex: 1,
        justifyContent: 'center',
        alignContent: 'center'
    },
    container: {
        flex: 1,
        padding: 16,
        gap: 20
    },
    blockSize: {
        width: 97,
        height: 97,
        borderRadius: 8
    },
    invalid: {
        borderColor: 'red',
    },
    blockStyle: {
        backgroundColor: '#C6C6CE',
    },
    alertText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        fontWeight: 'bold',
    },
    input: {
        backgroundColor: theme.colors.field.background,
        borderColor: theme.colors.field.border,
        borderWidth: 1,
        width: '100%',
        height: 40,
        padding: 10,
        borderRadius: 4,
    },
    multiline: {
        minHeight: 50,
        height: 200,
        zIndex: -11
    },
    addAttachment: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    cameraAttachmentIcon: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    textHint: {
        fontSize: 12,
        fontWeight: '500',
        color: theme.colors.red
    }
}));
